import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from './siteSettings';

/* ════════════════════════════════════════════════════════════
   HERO 3D — champ de panneaux solaires animé (Three.js via CDN)
   - les modules « se posent » un par un à l'ouverture
   - un soleil se déplace et fait briller les panneaux
   - des particules d'énergie circulent le long des rangées
   - la caméra suit la souris (parallax)
   Si la 3D n'est pas disponible, onFail() affiche le hero photo.
   ════════════════════════════════════════════════════════════ */

const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.149.0/build/three.min.js';

function loadThree() {
  if (window.THREE) return Promise.resolve(window.THREE);
  if (window.__threeP) return window.__threeP;
  window.__threeP = new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = THREE_URL;
    s.async = true;
    s.onload = () => (window.THREE ? res(window.THREE) : rej(new Error('three')));
    s.onerror = rej;
    document.head.appendChild(s);
  });
  return window.__threeP;
}

function panelTexture(THREE) {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 160;
  const g = c.getContext('2d');
  const grd = g.createLinearGradient(0, 0, 256, 160);
  grd.addColorStop(0, '#0d2a78'); grd.addColorStop(1, '#061640');
  g.fillStyle = grd; g.fillRect(0, 0, 256, 160);
  g.strokeStyle = 'rgba(160,190,255,.35)'; g.lineWidth = 1.2;
  for (let x = 0; x <= 256; x += 256 / 10) { g.beginPath(); g.moveTo(x, 0); g.lineTo(x, 160); g.stroke(); }
  for (let y = 0; y <= 160; y += 160 / 6) { g.beginPath(); g.moveTo(0, y); g.lineTo(256, y); g.stroke(); }
  g.strokeStyle = '#c9d2e3'; g.lineWidth = 8; g.strokeRect(0, 0, 256, 160);
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
}

function glowTexture(THREE, color) {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d');
  const r = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  r.addColorStop(0, color); r.addColorStop(0.25, color); r.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = r; g.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

function Scene({ onFail }) {
  const wrap = useRef();

  useEffect(() => {
    let raf, renderer, alive = true, visible = true;
    const cleanups = [];
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    loadThree().then((THREE) => {
      if (!alive || !wrap.current) return;
      const el = wrap.current;
      const W = () => el.clientWidth, H = () => el.clientHeight;
      const mobile = W() < 700;

      try {
        renderer = new THREE.WebGLRenderer({ antialias: !mobile, alpha: true, powerPreference: 'high-performance' });
      } catch (e) {
        try { renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true }); } catch (e2) { onFail(); return; }
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2));
      renderer.setSize(W(), H());
      renderer.outputEncoding = THREE.sRGBEncoding;
      el.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      scene.fog = new THREE.Fog(0x07123a, 18, 46);
      const camera = new THREE.PerspectiveCamera(mobile ? 58 : 42, W() / H(), 0.1, 200);
      const camBase = new THREE.Vector3(mobile ? 0 : -3.5, mobile ? 9 : 7.5, mobile ? 14 : 17);
      camera.position.copy(camBase);
      const target = new THREE.Vector3(mobile ? 0 : 3, mobile ? -3 : 0, 0);

      // Lumières
      scene.add(new THREE.HemisphereLight(0x8fb2ff, 0x0b1b4d, 0.55));
      const sunLight = new THREE.DirectionalLight(0xffe2a0, 1.6);
      scene.add(sunLight);
      const fill = new THREE.PointLight(0x2f6bff, 0.8, 40);
      fill.position.set(-8, 4, 8); scene.add(fill);

      // Sol : grille bleue
      const grid = new THREE.GridHelper(80, 80, 0x2a4fbf, 0x16307a);
      grid.material.transparent = true; grid.material.opacity = 0.35;
      grid.position.y = -1.2; scene.add(grid);

      // Soleil
      const sun = new THREE.Mesh(new THREE.SphereGeometry(0.9, 32, 32), new THREE.MeshBasicMaterial({ color: 0xffd34d }));
      scene.add(sun);
      const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTexture(THREE, 'rgba(255,200,60,0.9)'), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
      halo.scale.set(9, 9, 1); sun.add(halo);

      // Panneaux (instanciés)
      const rows = mobile ? 5 : 7, cols = mobile ? 6 : 11;
      const pw = 1.7, ph = 1.05, gapX = 0.12, gapZ = 2.3;
      const tex = panelTexture(THREE);
      const panelGeo = new THREE.BoxGeometry(pw, 0.06, ph);
      const panelMat = new THREE.MeshStandardMaterial({ map: tex, emissiveMap: tex, emissive: 0x3355aa, emissiveIntensity: 0.55, metalness: 0.45, roughness: 0.25, color: 0xffffff });
      const count = rows * cols;
      const panels = new THREE.InstancedMesh(panelGeo, panelMat, count);
      scene.add(panels);
      const legGeo = new THREE.CylinderGeometry(0.035, 0.035, 1, 6);
      const legMat = new THREE.MeshStandardMaterial({ color: 0x9aa6bd, metalness: 0.8, roughness: 0.35 });
      const legs = new THREE.InstancedMesh(legGeo, legMat, count * 2);
      scene.add(legs);

      const tilt = 0.42;
      const offX = ((cols - 1) * (pw + gapX)) / 2, offZ = ((rows - 1) * gapZ) / 2;
      const base = [];
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
        base.push({ x: c * (pw + gapX) - offX + target.x, z: r * gapZ - offZ, d: (r * cols + c) * 0.045 + r * 0.12 });
      }
      const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), s3 = new THREE.Vector3(), p3 = new THREE.Vector3();

      // Particules d'énergie le long des rangées
      const perRow = mobile ? 14 : 26;
      const pCount = rows * perRow;
      const pGeo = new THREE.BufferGeometry();
      const pPos = new Float32Array(pCount * 3);
      const pSeed = new Float32Array(pCount);
      for (let i = 0; i < pCount; i++) pSeed[i] = Math.random();
      pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
      const pMat = new THREE.PointsMaterial({ size: mobile ? 0.28 : 0.22, map: glowTexture(THREE, 'rgba(255,190,40,1)'), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: 0xffc233 });
      const particles = new THREE.Points(pGeo, pMat);
      scene.add(particles);
      const spanX = cols * (pw + gapX);

      // Souris
      const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
      const onMove = (ev) => {
        const r = el.getBoundingClientRect();
        mouse.tx = ((ev.clientX - r.left) / r.width - 0.5) * 2;
        mouse.ty = ((ev.clientY - r.top) / r.height - 0.5) * 2;
      };
      window.addEventListener('mousemove', onMove, { passive: true });
      cleanups.push(() => window.removeEventListener('mousemove', onMove));

      const onResize = () => { renderer.setSize(W(), H()); camera.aspect = W() / H(); camera.updateProjectionMatrix(); };
      window.addEventListener('resize', onResize);
      cleanups.push(() => window.removeEventListener('resize', onResize));

      const io = new IntersectionObserver(([en]) => { visible = en.isIntersecting; }, { threshold: 0 });
      io.observe(el); cleanups.push(() => io.disconnect());

      const start = performance.now();
      const ease = (t) => 1 - Math.pow(1 - Math.min(Math.max(t, 0), 1), 3);

      const frame = (now) => {
        raf = requestAnimationFrame(frame);
        if (!visible || document.hidden) return;
        const t = reduce ? 30 : (now - start) / 1000;

        // Soleil en arc
        const a = 0.9 + Math.sin(t * 0.12) * 0.55;
        sun.position.set(Math.cos(a) * 16 + target.x, 7 + Math.sin(a) * 5, -14);
        sunLight.position.copy(sun.position);
        sunLight.target.position.copy(target);

        // Panneaux : animation de pose puis légère ondulation
        let li = 0;
        for (let i = 0; i < count; i++) {
          const b = base[i];
          const k = ease((t - 0.3 - b.d) / 0.9);
          const y = -2.5 + k * 2.5 + Math.sin(t * 1.2 + b.x * 0.35 + b.z * 0.5) * 0.04 * k;
          e.set(tilt * k + (1 - k) * 1.2, 0, 0); q.setFromEuler(e);
          s3.set(1, 1, 1).multiplyScalar(0.3 + 0.7 * k);
          p3.set(b.x, y, b.z);
          m4.compose(p3, q, s3); panels.setMatrixAt(i, m4);
          // pieds avant / arrière
          for (const [dz, hgt] of [[-0.42, 1.08], [0.42, 0.62]]) {
            e.set(0, 0, 0); q.setFromEuler(e);
            s3.set(1, hgt * k + 0.001, 1);
            p3.set(b.x, -1.2 + (hgt * k) / 2, b.z + dz);
            m4.compose(p3, q, s3); legs.setMatrixAt(li++, m4);
          }
        }
        panels.instanceMatrix.needsUpdate = true;
        legs.instanceMatrix.needsUpdate = true;

        // Particules
        const on = ease((t - 2.2) / 1.2);
        for (let r = 0; r < rows; r++) for (let j = 0; j < perRow; j++) {
          const i = r * perRow + j;
          const f = (pSeed[i] + t * 0.08 * (0.6 + pSeed[i])) % 1;
          pPos[i * 3] = target.x - spanX / 2 + f * spanX;
          pPos[i * 3 + 1] = -0.55 + Math.sin(f * Math.PI * 6 + r) * 0.05;
          pPos[i * 3 + 2] = r * gapZ - offZ + 0.55;
        }
        pGeo.attributes.position.needsUpdate = true;
        pMat.opacity = on;

        // Caméra : dérive lente + parallax souris
        mouse.x += (mouse.tx - mouse.x) * 0.04; mouse.y += (mouse.ty - mouse.y) * 0.04;
        camera.position.set(
          camBase.x + Math.sin(t * 0.08) * 1.6 + mouse.x * 1.8,
          camBase.y + mouse.y * -0.9,
          camBase.z + Math.cos(t * 0.08) * 0.8
        );
        camera.lookAt(target);
        renderer.render(scene, camera);
      };
      raf = requestAnimationFrame(frame);
    }).catch((err) => { onFail(); });

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      cleanups.forEach(f => f());
      if (renderer) { renderer.dispose(); if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement); }
    };
  }, [onFail]);

  return <div ref={wrap} className="h3-canvas" aria-hidden="true" />;
}

const CSS = `
.h3{position:relative;height:calc(100vh - 126px);min-height:600px;max-height:900px;overflow:hidden;background:radial-gradient(120% 90% at 75% 20%,#1d4ed8 0%,#0b1b4d 45%,#050c2b 100%)}
.h3-canvas{position:absolute;inset:0}
.h3-canvas canvas{display:block;width:100%!important;height:100%!important}
.h3-shade{position:absolute;inset:0;pointer-events:none;background:linear-gradient(90deg,rgba(5,12,43,.95) 0%,rgba(5,12,43,.82) 36%,rgba(5,12,43,.25) 58%,rgba(5,12,43,0) 72%),linear-gradient(0deg,rgba(5,12,43,.7) 0%,rgba(5,12,43,0) 30%)}
.h3-content{position:absolute;inset:0;display:flex;align-items:center;pointer-events:none}
.h3-content .ev-wrap{width:100%}
.h3-inner{max-width:780px;pointer-events:auto}
.h3-kick{display:inline-flex;align-items:center;gap:10px;font-family:var(--ev-h);font-weight:700;font-size:.8rem;letter-spacing:.22em;text-transform:uppercase;color:var(--ev-sun);margin-bottom:18px}
.h3-kick i{width:8px;height:8px;border-radius:50%;background:var(--ev-sun);box-shadow:0 0 12px var(--ev-sun);animation:h3p 1.8s infinite}
@keyframes h3p{50%{opacity:.3}}
.h3-title{font-weight:900;font-size:clamp(2.3rem,5.2vw,4.3rem);line-height:1.02;color:#fff;text-transform:uppercase;letter-spacing:-.01em}
.h3-title span{background:linear-gradient(90deg,#F5B301,#ffe08a,#F5B301);background-size:200% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:h3g 4s linear infinite}
@keyframes h3g{to{background-position:200% 0}}
.h3-sub{color:rgba(255,255,255,.8);font-size:1.08rem;line-height:1.8;margin:22px 0 34px;max-width:540px}
.h3-in>*{opacity:0;transform:translateY(26px);animation:h3in .9s cubic-bezier(.16,1,.3,1) forwards}
.h3-in>*:nth-child(2){animation-delay:.12s}.h3-in>*:nth-child(3){animation-delay:.24s}.h3-in>*:nth-child(4){animation-delay:.36s}
@keyframes h3in{to{opacity:1;transform:none}}
.h3-cards{position:absolute;right:4%;bottom:110px;display:flex;flex-direction:column;gap:14px;z-index:3}
.h3-card{display:flex;align-items:center;gap:14px;padding:14px 20px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.18);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);color:#fff;border-radius:6px;box-shadow:0 20px 40px rgba(0,0,0,.25);animation:h3f 6s ease-in-out infinite;opacity:0;animation-fill-mode:both}
.h3-card:nth-child(1){animation:h3in .8s .9s forwards,h3f 6s 1.7s ease-in-out infinite}
.h3-card:nth-child(2){animation:h3in .8s 1.1s forwards,h3f 6s 1.9s ease-in-out infinite;margin-right:40px}
.h3-card:nth-child(3){animation:h3in .8s 1.3s forwards,h3f 6s 2.1s ease-in-out infinite}
@keyframes h3f{50%{transform:translateY(-10px)}}
.h3-card b{font-family:var(--ev-h);font-weight:900;font-size:1.5rem;color:var(--ev-sun);line-height:1}
.h3-card small{font-size:.78rem;color:rgba(255,255,255,.75);line-height:1.35}
.h3-scroll{position:absolute;left:50%;bottom:78px;transform:translateX(-50%);width:26px;height:42px;border:2px solid rgba(255,255,255,.5);border-radius:14px;z-index:3}
.h3-scroll::after{content:'';position:absolute;left:50%;top:8px;width:4px;height:8px;margin-left:-2px;background:var(--ev-sun);border-radius:2px;animation:h3s 1.6s infinite}
@keyframes h3s{to{transform:translateY(14px);opacity:0}}
@media(max-width:1000px){.h3-cards{display:none}}
@media(max-width:700px){.h3-scroll{display:none}}
@media(max-width:700px){.h3{height:88vh}.h3-shade{background:linear-gradient(0deg,rgba(5,12,43,.97) 0%,rgba(5,12,43,.85) 50%,rgba(5,12,43,.2) 100%)}.h3-content{align-items:flex-end;padding-bottom:70px}}
`;

export function Hero3D({ Fallback }) {
  const [failed, setFailed] = useState(false);
  const S = useSiteSettings();
  const failRef = useRef(() => setFailed(true));

  useEffect(() => {
    let s = document.getElementById('ev-h3-css');
    if (!s) { s = document.createElement('style'); s.id = 'ev-h3-css'; document.head.appendChild(s); }
    s.textContent = CSS;
  }, []);

  if (failed && Fallback) return <Fallback />;

  return (
    <section className="h3">
      <Scene onFail={failRef.current} />
      <div className="h3-shade" />
      <div className="h3-content">
        <div className="ev-wrap">
          <div className="h3-inner h3-in">
            <span className="h3-kick"><i />Votre partenaire national · Installateurs & EPC</span>
            <h1 className="h3-title">Sous-traitance<br /><span>photovoltaïque</span></h1>
            <p className="h3-sub">Nous accompagnons les installateurs et EPC dans la réalisation de leurs chantiers photovoltaïques partout en France : hangars agricoles, toitures industrielles, ombrières et centrales au sol, de 100 kWc à 1 MWc.</p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/devis" className="ev-btn ev-btn-sun">Demander un devis</Link>
              <Link to="/references-photovoltaique" className="ev-btn ev-btn-ghost">Nos références</Link>
            </div>
            <Link to="/visite-technique" style={{ display: 'inline-block', marginTop: 14, color: 'rgba(255,255,255,.8)', fontSize: '.9rem' }}>🏠 Particulier en Nouvelle-Aquitaine ? Visite gratuite →</Link>
          </div>
        </div>
      </div>
      <div className="h3-cards" aria-hidden="true">
        <div className="h3-card"><b>{S.mwc}</b><small>{S.mwc_label}</small></div>
        <div className="h3-card"><b>1 MWc</b><small>par chantier<br />jusqu'à</small></div>
        <div className="h3-card"><b>{S.chiffrage}</b><small>pour un<br />chiffrage</small></div>
      </div>
      <div className="h3-scroll" aria-hidden="true" />
    </section>
  );
}

export default Hero3D;
